/**
 * PlanetLayerGenerator — the INNER "planet layer" map.
 *   • Central planet at (0,0) — the hub (not mined).
 *   • Moons: 0..4 orbiting the planet; each moon can have 0..2 sub-moons
 *     orbiting IT (a small nested hierarchy). Moons + sub-moons are mineable.
 *   • Asteroids: the classic scattered belt, static, mineable.
 *
 * `sprite` fields are 0..1 seeds the render layer maps to an atlas frame.
 */
import { Vector2 } from "@/math/Vector2";
import { makeRng } from "@/math/rng";
import { MOON_TURRET } from "@/config/balance";
import {
  PLANET_TYPES,
  type PlanetType,
  type PlanetService,
} from "./SolarSystemGenerator";
import type { MineableBody } from "./body";

/** Auto-turret mounted on a moon of a rogue planet. Destructible (its own HP);
 * the moon can't be mined until its turret is gone. Satisfies `Damageable`. */
export interface MoonTurret {
  moonId: string;
  body: MineableBody; // the moon it sits on (turret stays centred on it)
  pos: Vector2; // = moon centre, refreshed each frame
  radius: number; // ~40% of the moon radius
  hp: number;
  maxHp: number;
  shield: number; // always 0 (kept for the Damageable shape)
  maxShield: number;
  shieldFlash: number;
  hitTimer: number;
  angle: number; // 360° aim
  cooldown: number;
  flash: number;
  aggro: boolean;
  damage: number; // bolt damage (scales with moon size)
  dead: boolean;
}

export interface SubMoon extends MineableBody {
  angle: number;
  distance: number; // orbit radius around the parent MOON
  orbitalSpeed: number;
  sprite: number;
}

export interface Moon extends MineableBody {
  angle: number;
  distance: number; // orbit radius around the central planet
  orbitalSpeed: number;
  sprite: number;
  subMoons: SubMoon[];
}

export type FieldAsteroid = MineableBody;

export interface CentralPlanet {
  pos: Vector2;
  radius: number;
  color: number;
  type: PlanetType;
  label: string;
  sprite: number;
}

const CENTRAL_RADIUS = 130;
const MOON_FIRST_ORBIT = 240;
const MOON_GAP = 120;

export class PlanetLayerManager {
  planet: CentralPlanet;
  moons: Moon[] = [];
  asteroids: FieldAsteroid[] = [];
  moonOrbitRadii: number[] = [];
  readonly bodies: MineableBody[] = [];
  readonly destroyed = new Set<string>(); // ids of bodies mined out (persist per planet)
  readonly service: PlanetService;
  readonly rogue: boolean;
  readonly turrets: MoonTurret[] = []; // rogue-planet moon turrets
  moonsFrozen = false; // set when the Eater of Worlds awakens → all moons stop orbiting

  constructor(
    seed: number,
    type: PlanetType,
    color: number,
    planetSprite: number,
    service: PlanetService = "none",
    maxMoons = 4,
    maxSubMoons = 2,
    rogue = false,
    turretChance = 0,
    sizeMul = 1, // deeper-space growth: spreads the moons + belt wider and adds more rocks
  ) {
    this.service = service;
    this.rogue = rogue;
    const oreMul = rogue ? MOON_TURRET.resourceMul : 1; // rogue worlds are 3× richer
    const rng = makeRng(seed);
    const def = PLANET_TYPES[type];
    this.planet = {
      pos: new Vector2(0, 0),
      radius: CENTRAL_RADIUS,
      color,
      type,
      label: def.label,
      sprite: planetSprite,
    };

    // --- moons: 0..maxMoons (same first RNG draw as the system view → counts match) ---
    const moonCount = rng.int(0, maxMoons);
    let r = MOON_FIRST_ORBIT * sizeMul;
    for (let i = 0; i < moonCount; i++) {
      if (i > 0) r += (MOON_GAP + rng.range(0, 90)) * sizeMul;
      const angle = rng.range(0, Math.PI * 2);
      const dir = rng.next() < 0.5 ? 1 : -1;
      const orbitalSpeed = dir * (0.05 + 12 / r) * (0.6 + rng.next() * 0.6);
      const oreMax = rng.range(700, 1700) * oreMul;
      const moonRadius = rng.range(24, 42);
      const moon: Moon = {
        id: `moon_${i}`,
        angle,
        distance: r,
        orbitalSpeed,
        radius: moonRadius,
        sprite: rng.next(),
        pos: new Vector2(Math.cos(angle) * r, Math.sin(angle) * r),
        ore: oreMax,
        oreMax,
        subMoons: [],
      };

      // --- sub-moons: 0..maxSubMoons orbiting this moon ---
      const subCount = rng.int(0, maxSubMoons);
      let sr = moonRadius + 34;
      for (let j = 0; j < subCount; j++) {
        if (j > 0) sr += 26 + rng.range(0, 18);
        const sa = rng.range(0, Math.PI * 2);
        const sdir = rng.next() < 0.5 ? 1 : -1;
        const sOreMax = rng.range(150, 500) * oreMul;
        moon.subMoons.push({
          id: `moon_${i}_sub_${j}`,
          angle: sa,
          distance: sr,
          orbitalSpeed: sdir * (0.5 + rng.next() * 0.8),
          radius: rng.range(9, 15),
          sprite: rng.next(),
          pos: new Vector2(
            moon.pos.x + Math.cos(sa) * sr,
            moon.pos.y + Math.sin(sa) * sr,
          ),
          ore: sOreMax,
          oreMax: sOreMax,
        });
      }

      this.moons.push(moon);
      this.moonOrbitRadii.push(r);
    }

    // --- asteroid belt (classic scattered field) ---
    const beltInner = r + 170 * sizeMul;
    const beltOuter = beltInner + rng.range(520, 950) * sizeMul;
    const count = Math.round(rng.int(34, 60) * sizeMul); // more asteroids fill the bigger belt
    for (let i = 0; i < count; i++) {
      const a = rng.range(0, Math.PI * 2);
      const dd = rng.range(beltInner, beltOuter);
      const oreMax = rng.range(220, 1200) * oreMul;
      this.asteroids.push({
        id: `ast_${i}`,
        pos: new Vector2(Math.cos(a) * dd, Math.sin(a) * dd),
        radius: rng.range(14, 46),
        ore: oreMax,
        oreMax,
      });
    }

    // --- small scattered asteroids: quick, low-ore rocks spread across the whole
    // disc (from just past the planet out to the belt) so early game always has
    // fast, nearby targets. ---
    const smallCount = Math.round(rng.int(26, 40) * sizeMul);
    const smallInner = CENTRAL_RADIUS + 110 * sizeMul;
    for (let i = 0; i < smallCount; i++) {
      const a = rng.range(0, Math.PI * 2);
      const dd = rng.range(smallInner, beltOuter);
      const oreMax = rng.range(40, 150) * oreMul;
      this.asteroids.push({
        id: `sast_${i}`,
        pos: new Vector2(Math.cos(a) * dd, Math.sin(a) * dd),
        radius: rng.range(11, 19),
        ore: oreMax,
        oreMax,
      });
    }

    // flat mineable list (stable; positions mutate in place on update)
    for (const m of this.moons) {
      this.bodies.push(m);
      for (const s of m.subMoons) this.bodies.push(s);
    }
    for (const a of this.asteroids) this.bodies.push(a);

    // --- rogue moon turrets: some moons (big OR sub) mount a 360° auto-turret ---
    if (rogue && turretChance > 0) {
      const mkTurret = (body: MineableBody): MoonTurret => {
        const maxHp = Math.round(body.radius * MOON_TURRET.hpPerRadius);
        return {
          moonId: body.id,
          body,
          pos: new Vector2(body.pos.x, body.pos.y),
          radius: body.radius * MOON_TURRET.sizeMul,
          hp: maxHp,
          maxHp,
          shield: 0,
          maxShield: 0,
          shieldFlash: 0,
          hitTimer: 0,
          angle: rng.range(0, Math.PI * 2),
          cooldown: rng.range(0, MOON_TURRET.fireInterval),
          flash: 0,
          aggro: false,
          damage: Math.max(3, Math.round(body.radius * MOON_TURRET.dmgPerRadius)),
          dead: false,
        };
      };
      for (const m of this.moons) {
        if (rng.next() < turretChance) this.turrets.push(mkTurret(m));
        for (const s of m.subMoons) {
          if (rng.next() < turretChance) this.turrets.push(mkTurret(s));
        }
      }
    }
  }

  /** Revolve moons around the planet, and sub-moons around their moon. */
  update(dt: number): void {
    if (this.moonsFrozen) return; // the Eater's awakening halted every moon in place
    for (const m of this.moons) {
      // A mined-out moon has no mass left: it and its sub-moons stop orbiting
      // (their gravitational anchor is gone) and freeze where they are.
      if (m.ore <= 0) continue;
      m.angle += m.orbitalSpeed * dt;
      m.pos.set(Math.cos(m.angle) * m.distance, Math.sin(m.angle) * m.distance);
      for (const s of m.subMoons) {
        s.angle += s.orbitalSpeed * dt;
        s.pos.set(
          m.pos.x + Math.cos(s.angle) * s.distance,
          m.pos.y + Math.sin(s.angle) * s.distance,
        );
      }
    }
    // Keep each live turret centred on its (now-revolved) moon.
    for (const t of this.turrets) {
      if (!t.dead) t.pos.set(t.body.pos.x, t.body.pos.y);
    }
  }

  byId(id: string): MineableBody | null {
    for (const b of this.bodies) if (b.id === id) return b;
    return null;
  }
}
